/**
 * The Analytics page's own API client, and the one place a metric becomes text.
 *
 * It does not go through `lib/api.ts`. That interface is implemented twice — once by the mock
 * adapter and once by `httpApi` — so every method added to it has to be added to both, and three
 * other agents are writing in this repository today. Analytics is a self-contained screen against
 * `/api/v1/analytics`, so its client lives beside it, the way the Config Studio's does.
 *
 * `formatMetric` used to be defined here, mirroring `src/report/format.ts` so the console and the
 * weekly report agree to the decimal place. `lib/format.ts` had a looser second implementation of
 * the same function, and Health rendered `first_reply_p50` as "4m" while this page rendered it as
 * "4.2 min". The server-matching version now lives in `lib/format.ts` as the only one, and is
 * re-exported here so this page's imports and `test/Analytics.test.tsx` still read as one screen's
 * client.
 *
 * Author: Danish Khan
 */
import { ApiError } from '../lib/apiTypes.ts';

const BASE = '/api/v1/analytics';

export type MetricUnit = 'count' | 'ms' | 'percent' | 'inr';
export type MetricTemporal = 'point_in_time' | 'current_state';
export type MetricShape = 'scalar' | 'series';

export interface MetricDefinition {
  name: string;
  label: string;
  /** The SQL in metrics.definitions. Shown verbatim on hover — a Global Constraint. */
  sql: string;
  window: string;
  owner: string;
  unit: MetricUnit;
  /**
   * The tense the number is written in. Shown on every tile, because "last 30 days" and "right
   * now" are different questions and a reader who cannot tell which one they are looking at is a
   * reader who will quote the wrong one.
   */
  temporal: MetricTemporal;
  shape: MetricShape;
}

export interface MetricRow {
  bucket: string;
  label: string;
  value: number;
}

export interface MetricValue {
  name: string;
  /** Null when there is no number. Never rendered as a zero. */
  value: number | null;
  delta: number | null;
  rows: MetricRow[] | null;
  /** Why there is no number, in one sentence. Rendered in place of the figure. */
  unavailable: string | null;
  definition: MetricDefinition;
}

export interface AnalyticsPanel {
  id: string;
  title: string;
  /** What this panel is for, and what it deliberately does not claim. */
  note: string;
  metrics: MetricValue[];
}

export interface Analytics {
  panels: AnalyticsPanel[];
  lowerIsBetter: string[];
}

export interface AnalyticsClient {
  /** Every panel, or only the named ones. */
  panels(ids?: string[]): Promise<Analytics>;
}

async function call<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { credentials: 'same-origin' });
  const text = await res.text();
  let body: unknown = null;
  try { body = text ? JSON.parse(text) : null; } catch { /* handled below */ }
  if (!res.ok) {
    const detail = (body ?? {}) as { error?: { code?: string; message?: string } };
    throw new ApiError(detail.error?.message ?? `GET ${path} failed`, res.status,
      detail.error?.code ? { code: detail.error.code } : {});
  }
  return body as T;
}

export const analyticsApi: AnalyticsClient = {
  panels: (ids) => call<Analytics>(ids && ids.length > 0 ? `?panels=${ids.join(',')}` : ''),
};

/* ------------------------------------------------------------------ rendering a number */

/** One implementation, in `lib/format.ts`, so no two screens can disagree about one figure. */
export { formatDurationMs, formatMetric, temporalLabel } from '../lib/format.ts';
